// The PUT /employee/surveys/:id/answers body. Type-only imports, so the
// helpers stay plain functions with no runtime dependencies.
import type { QuestionType } from '@gokrew/models/survey';
import type {
  SurveyAnswerValue,
  EmployeeAnswerFile,
} from '@/types/employee-survey';

export function toSaveAnswerPayload(
  questionId: string,
  type: QuestionType,
  value: SurveyAnswerValue,
  files?: EmployeeAnswerFile[]
): Record<string, unknown> | null {
  switch (type) {
    case 'short_text':
    case 'paragraph':
    case 'email':
    case 'phone':
      return typeof value === 'string' && value !== ''
        ? { questionId, textValue: value }
        : null;
    case 'number':
    case 'rating':
    case 'linear_scale':
      return typeof value === 'number'
        ? { questionId, numericValue: value }
        : null;
    case 'date':
      return typeof value === 'string' && value !== ''
        ? { questionId, dateValue: value }
        : null;
    case 'time':
      return typeof value === 'string' && value !== ''
        ? { questionId, timeValue: value }
        : null;
    case 'yes_no':
      return value === 'Yes' || value === 'No'
        ? { questionId, booleanValue: value === 'Yes' }
        : null;
    case 'single_choice':
    case 'dropdown':
    case 'image_choice':
      return typeof value === 'string' && value !== ''
        ? { questionId, selectedOptionIds: [value] }
        : null;
    case 'multiple_choice':
      return Array.isArray(value) && value.length > 0
        ? { questionId, selectedOptionIds: value }
        : null;
    case 'file_upload':
    case 'image_upload':
      return files && files.length > 0 ? { questionId, files } : null;
    default:
      return null;
  }
}

/**
 * The PUT /answers body. A question the server has an answer for that the
 * employee has since emptied gets an explicit `{ questionId, clear: true }`
 * (QA-07): leaving it out made the save skip it, and the old answer came
 * back on reload and was submitted. `savedQuestionIds` is what the server
 * holds (text-like answers and file answers alike); a question is only
 * cleared when the employee's state still has it and it is now empty.
 */
export function buildSaveAnswersPayload(
  answers: Record<string, SurveyAnswerValue>,
  questionTypes: Map<string, QuestionType>,
  fileAnswers: Record<string, EmployeeAnswerFile[]>,
  savedQuestionIds: ReadonlySet<string> = new Set()
): Record<string, unknown>[] {
  const payload: Record<string, unknown>[] = [];
  const included = new Set<string>();
  const push = (entry: Record<string, unknown>, questionId: string) => {
    payload.push(entry);
    included.add(questionId);
  };

  for (const [questionId, value] of Object.entries(answers)) {
    const type = questionTypes.get(questionId);
    if (!type) continue;
    const entry = toSaveAnswerPayload(
      questionId,
      type,
      value,
      fileAnswers[questionId]
    );
    if (entry) push(entry, questionId);
  }

  // File/image questions live in `fileAnswers`, not the flat value map.
  for (const [questionId, files] of Object.entries(fileAnswers)) {
    if (included.has(questionId)) continue;
    const type = questionTypes.get(questionId);
    if (!type) continue;
    const entry = toSaveAnswerPayload(questionId, type, undefined, files);
    if (entry) {
      push(entry, questionId);
    } else if (savedQuestionIds.has(questionId)) {
      // Every saved file was removed.
      push({ questionId, clear: true }, questionId);
    }
  }

  for (const questionId of Object.keys(answers)) {
    if (included.has(questionId) || !savedQuestionIds.has(questionId)) {
      continue;
    }
    if (!questionTypes.has(questionId)) continue;
    push({ questionId, clear: true }, questionId);
  }

  return payload;
}
